import {GesForm} from '@shared/interfaces/ges.interface';
import {Paperwork} from '@shared/interfaces/paperwork.interface';
import {format} from 'date-fns';

/**
 * Construye el contenido del PDF del formulario GES replicando el diseño
 * "Formulario de constancia información paciente GES" (body.html).
 *
 * Características del diseño:
 * - Títulos de sección con línea superior y tipografía mixta.
 * - Valores mostrados en cajas con fondo gris y borde.
 * - Sección "Información médica" dividida en GES y GES Oncológico.
 */

const CONTENT_WIDTH = 532;
const VALUE_FILL = '#EEF2F2';
const VALUE_BORDER = '#CCCECE';

/** Ancho fijo de las etiquetas de la sección "Información médica" (alineadas a la derecha). */
const MEDICAL_LABEL_WIDTH = 105;

/** Layout sin bordes para tablas contenedoras. */
const NO_BORDER_LAYOUT = {
  hLineWidth  : () => 0,
  vLineWidth  : () => 0,
  paddingLeft : () => 0,
  paddingRight: () => 0,
  paddingTop  : () => 0,
  paddingBottom: () => 0,
};

/** Layout para las cajas de valor (fondo gris con borde fino). */
const VALUE_LAYOUT = {
  hLineWidth  : () => 0.5,
  vLineWidth  : () => 0.5,
  hLineColor  : () => VALUE_BORDER,
  vLineColor  : () => VALUE_BORDER,
  paddingLeft : () => 4,
  paddingRight: () => 4,
  paddingTop  : () => 2,
  paddingBottom: () => 2,
};

/** Layout para los recuadros de selección (checkbox). */
const CHECKBOX_LAYOUT = {
  hLineWidth  : () => 0.7,
  vLineWidth  : () => 0.7,
  hLineColor  : () => '#000',
  vLineColor  : () => '#000',
  paddingLeft : () => 0,
  paddingRight: () => 0,
  paddingTop  : () => 0,
  paddingBottom: () => 0,
};

/** Etiqueta simple. */
const label = (text: string, margin = [0, 5, 4, 0]) => ({text, fontSize: 9, margin});

/** Caja de valor con fondo gris. */
const valueBox = (text: string) => {
  const content = (text === undefined || text === null || `${text}`.trim() === '') ? ' ' : text;
  return {
    table : {
      widths : ['*'],
      heights: [11],
      body   : [[{text: content, bold: true, fontSize: 9, fillColor: VALUE_FILL}]],
    },
    layout: VALUE_LAYOUT,
  };
};

/** Recuadro de selección (X cuando está marcado). */
const checkBox = (checked: boolean) => ({
  width : 'auto',
  table : {
    widths : [12],
    heights: [12],
    body   : [[{text: checked ? 'X' : '', alignment: 'center', bold: true, fontSize: 9, fillColor: VALUE_FILL}]],
  },
  layout: CHECKBOX_LAYOUT,
  margin: [0, 3, 0, 0],
});

/** Título de sección con línea superior. */
const sectionTitle = (text: string) => ([
  {
    canvas: [{type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1.5, lineColor: '#000'}],
    margin: [0, 5, 0, 0],
  },
  {text, bold: true, fontSize: 13, margin: [0, 2, 0, 2]},
]);

/** Fila con una etiqueta y una caja de valor. */
const labelValueRow = (labelText: string, value: string, labelWidth: string | number, margin = [0, 2, 0, 0]) => ({
  columns: [
    {width: labelWidth, ...label(labelText)},
    {width: '*', stack: [valueBox(value)]},
  ],
  margin,
});

export const buildContentPdfGesV2 = (paperwork: Paperwork, group) => {
  const {patient, professional} = paperwork;
  const {rows} = group;
  const profesionalName = (`${professional?.names || ''} ${professional?.surnames || ''}`).trim();

  const buildPdfGes = rows.map((item: GesForm) => {
    const {
            center,
            lender,
            beneficiary,
            namePathology,
            type,
            attention,
            notification,
            oncologic,
          } = item;

    const centerAddress  = center?.address?.street || lender?.address || '';
    const centerLocality = center?.address?.locality || lender?.city || '';
    const region         = (beneficiary?.region || '').replace(/\d+/g, '');
    const isVirtual      = attention === 'teleconsulta' || center?.name?.toLowerCase() === 'centro médico virtual';
    const isFonasa       = beneficiary?.forecast === 'Fonasa';
    const isIsapre       = beneficiary?.forecast === 'Isapre';

    return [
      // Título del documento
      {
        text     : [
          {text: 'Formulario de constancia información paciente GES\n', bold: true, fontSize: 16},
          {text: '(Artículo 24°, Ley 19.966)', fontSize: 11},
        ],
        alignment : 'center',
        lineHeight: 1.1,
        margin    : [0, 0, 0, 2],
      },

      // ---------------- Datos del prestador ----------------
      ...sectionTitle('Datos del prestador'),
      labelValueRow('Institución (Hospital, Clínica, Consultorio, etc.):', `${center?.name || ''}`, '37%'),
      {
        columns: [
          {width: '10%', ...label('Dirección:')},
          {width: '52%', stack: [valueBox(centerAddress)]},
          {width: '3%', text: ''},
          {width: '7%', ...label('Ciudad:')},
          {width: '28%', stack: [valueBox(centerLocality)]},
        ],
        margin : [0, 2, 0, 0],
      },
      labelValueRow('Nombre persona que notifica:', profesionalName, '24%'),
      {
        columns: [
          {width: '5%', ...label('RUN:')},
          {width: '20%', stack: [valueBox(`${lender?.documentNumber || ''}`)]},
          {width: '*', text: ''},
        ],
        margin : [0, 2, 0, 0],
      },

      // ---------------- Antecedentes del/la paciente ----------------
      ...sectionTitle('Antecedentes del/la paciente'),
      labelValueRow('Nombre legal:', `${patient?.names || ''} ${patient?.surnames || ''}`, '14%'),
      labelValueRow('Nombre social:', `${beneficiary?.socialName ? beneficiary.socialName.toUpperCase() : ''}`, '14%'),
      {
        columns: [
          {width: '8%', ...label('RUN:')},
          {width: '26%', stack: [valueBox(`${patient?.documentNumber || ''}`)]},
          {width: '4%', text: ''},
          {width: '10%', ...label('Previsión:')},
          {width: '9%', ...label('Fonasa')},
          checkBox(isFonasa),
          {width: '2%', text: ''},
          {width: '9%', ...label('Isapre')},
          checkBox(isIsapre),
          {width: '3%', text: ''},
        ],
        margin : [0, 2, 0, 0],
      },
      {
        columns: [
          {width: '10%', ...label('Dirección:')},
          {width: '52%', stack: [valueBox(`${beneficiary?.address || ''}`)]},
          {width: '3%', text: ''},
          {width: '10%', ...label('Comuna:')},
          {width: '25%', stack: [valueBox(`${beneficiary?.commune || ''}`)]},
        ],
        margin : [0, 2, 0, 0],
      },
      {
        columns: [
          {width: '7%', ...label('Región:')},
          {width: '22%', stack: [valueBox(region)]},
          {width: '8%', ...label('Teléfono:')},
          {width: '18%', stack: [valueBox(`${patient?.phone || ''}`)]},
          {width: '16%', ...label('Correo electrónico:')},
          {width: '*', stack: [valueBox(`${patient?.email || ''}`)]},
        ],
        margin : [0, 2, 0, 0],
      },

      // ---------------- Información médica ----------------
      ...sectionTitle('Información médica'),
      {
        columns: [
          {width: MEDICAL_LABEL_WIDTH, text: 'Problema de Salud GES:', fontSize: 9, alignment: 'right', margin: [0, 5, 6, 0]},
          {width: '*', stack: [valueBox(oncologic ? '' : namePathology)]},
        ],
        margin : [0, 2, 0, 0],
      },
      {
        columns: [
          {width: MEDICAL_LABEL_WIDTH, text: 'Confirmación', fontSize: 9, alignment: 'right', margin: [0, 3, 6, 0]},
          checkBox(!oncologic),
        ],
        margin : [0, 2, 0, 0],
      },
      {
        canvas: [{type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1.5, lineColor: '#000'}],
        margin: [0, 6, 0, 0],
      },
      labelValueRow('Problema de Salud GES Oncológico:', oncologic ? namePathology : '', '30%', [0, 4, 0, 0]),
      {
        columns: [
          {width: 'auto', ...label('Sospecha', [0, 3, 4, 0])},
          checkBox(oncologic && type === 'sospecha'),
          {width: '*', text: ''},
          {width: 'auto', ...label('Confirmación', [0, 3, 4, 0])},
          checkBox(oncologic && type === 'confirmacion'),
          {width: '*', text: ''},
          {width: 'auto', ...label('Etapificación', [0, 3, 4, 0])},
          checkBox(oncologic && type === 'diagnostico'),
          {width: '*', text: ''},
          {width: 'auto', ...label('Tratamiento', [0, 3, 4, 0])},
          checkBox(oncologic && type === 'tratamiento'),
          {width: '*', text: ''},
          {width: 'auto', ...label('Seguimiento', [0, 3, 4, 0])},
          checkBox(oncologic && type === 'seguimiento'),
          {width: '*', text: ''},
          {width: 'auto', ...label('Rehabilitación', [0, 3, 4, 0])},
          checkBox(oncologic && type === 'rehabilitacion'),
        ],
        margin : [0, 4, 0, 0],
      },
      {
        canvas: [{type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1.5, lineColor: '#000'}],
        margin: [0, 6, 0, 0],
      },
      {
        columns: [
          {width: 'auto', text: 'Tipo de atención', bold: true, fontSize: 11, margin: [0, 4, 10, 0]},
          {width: 'auto', ...label('Presencial', [0, 5, 4, 0])},
          checkBox(!isVirtual),
          {width: 'auto', ...label('Teleconsulta', [10, 5, 4, 0])},
          checkBox(isVirtual),
        ],
        margin : [0, 4, 0, 0],
      },

      // ---------------- Constancia ----------------
      ...sectionTitle('Constancia:'),
      {
        text     : 'Tomo conocimiento que tengo derecho a acceder a las Garantías Explícitas en Salud, en la medida que me ' +
          'atienda en la red de Prestadores que asigne el Fonasa o la Isapre, según corresponda.',
        fontSize : 9,
        alignment: 'justify',
        margin   : [0, 0, 0, 8],
      },
      {
        columns: [
          {width: '25%', ...label('Fecha y hora de notificación:', [0, 5, 4, 0])},
          {width: '*', stack: [valueBox(`${format(new Date(), `dd-MM-yyyy 'a las' HH:mm`)}`)]},
        ],
        margin : [0, 2, 0, 105],
      },

      // ---------------- Pie / medios de notificación ----------------
      {
        text     : [
          {text: '* En la modalidad de TELECONSULTA, '},
          {text: 'en ausencia de la firma o huella', decoration: 'underline'},
          {text: ', se registrará el medio a través del cual el/la paciente o su representante tomó conocimiento:'},
        ],
        bold     : true,
        fontSize : 9,
        alignment: 'justify',
        margin   : [0, 0, 0, 4],
      },
      {
        columns: [
          {width: 'auto', ...label('Correo electrónico', [0, 3, 4, 0])},
          checkBox(true),
          {width: 'auto', ...label('Carta certificada', [12, 3, 4, 0])},
          checkBox(notification === 'cartacertificada'),
          {width: 'auto', ...label('Otros (indicar)', [12, 3, 4, 0])},
          {width: '*', stack: [valueBox('')]},
        ],
        margin : [0, 2, 0, 6],
      },
      {
        text    : 'En caso que la persona que tomó conocimiento no sea el/la paciente, identificar:',
        bold    : true,
        fontSize: 10,
        margin  : [0, 4, 0, 4],
      },
      {
        columns: [
          {width: '10%', ...label('Nombre')},
          {width: '61%', stack: [valueBox('')]},
          {width: '7%', ...label(' RUN')},
          {width: '22%', stack: [valueBox('')]},
        ],
        margin : [0, 2, 0, 0],
      },
      {
        columns: [
          {width: '10%', ...label('Teléfono')},
          {width: '22%', stack: [valueBox('')]},
          {width: '18%', ...label('Correo electrónico')},
          {width: '*', stack: [valueBox('')]},
        ],
        margin : [0, 2, 0, 6],
      },
      {
        text      : [
          {text: 'Importante: ', bold: true},
          {
            text: 'Tenga presente que si no se cumplen las garantías usted puede reclamar ante Fonasa o la Isapre, ' +
              'según corresponda. Si la respuesta no es satisfactoria, usted puede recurrir en segunda instancia a la ' +
              'Superintendencia de Salud.',
            bold: true,
          },
        ],
        fontSize  : 9,
        lineHeight: 1,
        alignment : 'justify',
        margin    : [0, 4, 0, 0],
      },
    ];
  });

  return [buildPdfGes];
};
